import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Checkbox, Typography, Input, Modal, ModalContent, ModalActions } from '@braskai/ui-kit';
import { FormControlLabel, Stack } from '@mui/material';
import { ChangeEvent } from 'react';
import { Form, FormField } from '@/shared/ui/form/Form';
import { ModalHeaderDefault } from '@/shared/ui/modal';

const formSchema = z.object({
  options: z.array(z.string()).min(1),
  comment: z.string().min(1),
});

type FormValues = z.infer<typeof formSchema>;

type FeedbackFormProps = {
  onSubmit: (data: FormValues) => void;
  onClose: () => void;
  options: string[];
  isOpen: boolean;
};

export const TtsFeedbackModal = ({ onSubmit, onClose, options, isOpen }: FeedbackFormProps) => {
  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      options: [],
      comment: '',
    },
  });

  const {
    handleSubmit,
    formState: { isValid, isSubmitting },
    control,
  } = methods;

  return (
    <Modal onClose={isSubmitting ? undefined : onClose} open={isOpen}>
      <ModalHeaderDefault title="What went wrong?" isCloseButtonDisabled={isSubmitting} onClose={onClose} />
      <Form {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalContent>
            <Stack gap={20}>
              <Stack gap={8}>
                <FormField
                  name="options"
                  control={control}
                  render={({ field }) => (
                    <>
                      {options.map((issue) => (
                        <FormControlLabel
                          key={issue}
                          control={
                            <Checkbox
                              checked={field.value.includes(issue)}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                field.onChange(
                                  e.target.checked
                                    ? [...field.value, issue]
                                    : field.value.filter((value) => value !== issue),
                                );
                              }}
                            />
                          }
                          label={
                            <Typography variant="body-2-md" color="grey.700">
                              {issue}
                            </Typography>
                          }
                          sx={(theme) => ({
                            margin: 0,
                            gap: 8,
                            padding: 10,
                            borderRadius: 10,
                            border: `1px solid ${theme.palette.grey[200]}`,
                          })}
                        />
                      ))}
                    </>
                  )}
                />
              </Stack>
              <FormField
                name="comment"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder="Feedback here"
                    multiline
                    rows={3}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            </Stack>
          </ModalContent>
          <ModalActions>
            <Button size="lg" variant="outlined" disabled={isSubmitting || !isValid} onClick={onClose} fullWidth>
              Cancel
            </Button>
            <Button size="lg" type="submit" disabled={isSubmitting || !isValid} isLoading={isSubmitting} fullWidth>
              Submit
            </Button>
          </ModalActions>
        </form>
      </Form>
    </Modal>
  );
};
